import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, map, of, tap } from 'rxjs';

import { environment } from '../../environments/environment';
import {
  MOCK_MAINTENANCE_JOBS,
  MOCK_REPAIR_HISTORY,
} from '../data/mock-maintenance.data';
import {
  MaintenanceJobsResponseDto,
  RepairHistoryResponseDto,
} from '../models/api.models';
import { ListQuery, PaginatedResult } from '../models/common.models';
import {
  MaintenanceJob,
  MaintenanceJobStats,
  MaintenanceJobStatus,
  MaintenanceJobType,
  MaintenancePriority,
  RepairHistoryEntry,
} from '../models/maintenance.models';
import { ApiService } from './api.service';
import {
  pick,
  pickDate,
  pickNumber,
  pickString,
  parseFormattedCurrency,
} from '../utils/api-shape.util';
import { newId } from '../utils/id.util';
import { AuthService } from '../../modules/auth/auth.service';

export interface MaintenanceApiResult {
  success: boolean;
  message?: string;
}

@Injectable({ providedIn: 'root' })
export class MaintenanceService {
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);

  private readonly jobs = signal<MaintenanceJob[]>(
    environment.useMockData ? [...MOCK_MAINTENANCE_JOBS] : [],
  );
  private readonly repairHistory = signal<RepairHistoryEntry[]>(
    environment.useMockData ? [...MOCK_REPAIR_HISTORY] : [],
  );

  readonly allJobs = this.jobs.asReadonly();
  readonly allRepairHistory = this.repairHistory.asReadonly();

  readonly jobStats = computed<MaintenanceJobStats>(() => {
    const list = this.jobs();
    return {
      totalJobs: list.length,
      inProgress: list.filter((job) => job.status === 'in-progress').length,
      completed: list.filter((job) => job.status === 'completed').length,
      pending: list.filter((job) => job.status === 'pending').length,
    };
  });

  constructor() {
    if (!environment.useMockData && sessionStorage.getItem('ims-auth')) {
      this.refreshFromApi();
    }
  }

  refreshFromApi(): void {
    if (environment.useMockData) {
      return;
    }

    this.api
      .get<MaintenanceJobsResponseDto>('/Maintenance/jobs')
      .pipe(
        tap((payload) => this.applyJobs(payload)),
        catchError(() => of(null)),
      )
      .subscribe();

    this.api
      .get<RepairHistoryResponseDto>('/Maintenance/repairs')
      .pipe(
        tap((payload) => this.applyRepairs(payload)),
        catchError(() => of(null)),
      )
      .subscribe();
  }

  fetchJobs(
    query: ListQuery = {},
  ): Observable<PaginatedResult<MaintenanceJob>> {
    return of(this.paginate(this.filterJobs(this.jobs(), query), query));
  }

  getJobsByStatus(status: MaintenanceJobStatus): MaintenanceJob[] {
    return this.jobs().filter((job) => job.status === status);
  }

  getRepairHistory(): RepairHistoryEntry[] {
    return this.repairHistory();
  }

  addJob(job: Omit<MaintenanceJob, 'id' | 'jobId'>): MaintenanceJob {
    const nextNumber = this.jobs().length + 1;
    const created: MaintenanceJob = {
      ...job,
      id: newId(),
      jobId: `MJ-2026-${String(nextNumber).padStart(3, '0')}`,
    };

    if (environment.useMockData) {
      this.jobs.update((list) => [created, ...list]);
      return created;
    }

    this.api
      .post('/Maintenance/jobs', {
        equipmentAssetID: Number(job.assetId) || 0,
        maintenanceType: job.type,
        problemDescription: job.description,
        estimatedCost: null,
        reportedByUserID: this.auth.currentUserId(),
        assignedToName: job.assignedTo,
      })
      .pipe(
        tap(() => this.refreshFromApi()),
        catchError(() => of(null)),
      )
      .subscribe();

    return created;
  }

  sendDamagedToMaintenance(payload: {
    equipmentAssetID?: number;
    equipmentReturnID: number;
    assignedToName: string;
    assignedToPhone?: string;
    problemDescription?: string;
    estimatedCost?: number;
  }): Observable<{ success: boolean; message?: string; jobId?: number }> {
    if (environment.useMockData) {
      this.addJob({
        assetName: 'Damaged item',
        assetId: String(payload.equipmentAssetID ?? payload.equipmentReturnID),
        type: 'repair',
        description: payload.problemDescription ?? 'Damage repair',
        priority: 'medium',
        assignedTo: payload.assignedToName,
        startDate: new Date().toISOString().slice(0, 10),
        status: 'pending',
      });
      return of({ success: true, message: 'Maintenance job created.' });
    }

    return this.api
      .post<{ success?: boolean; message?: string; jobId?: number }>(
        '/Maintenance/jobs',
        {
          equipmentAssetID:
            payload.equipmentAssetID && payload.equipmentAssetID > 0
              ? payload.equipmentAssetID
              : 0,
          maintenanceType: 'REPAIR',
          problemDescription:
            payload.problemDescription ?? 'Repair from damaged/lost report',
          estimatedCost: payload.estimatedCost ?? null,
          reportedByUserID: this.auth.currentUserId(),
          assignedToName: payload.assignedToName,
          assignedToPhone: payload.assignedToPhone ?? null,
          equipmentReturnID: payload.equipmentReturnID,
        },
      )
      .pipe(
        tap(() => this.refreshFromApi()),
        map((response) => ({
          success: response?.success !== false,
          message: response?.message ?? 'Maintenance job created successfully.',
          jobId: response?.jobId,
        })),
        catchError((error) =>
          of({
            success: false,
            message:
              error?.error?.error ??
              error?.error?.message ??
              error?.message ??
              'Failed to send item to maintenance.',
          }),
        ),
      );
  }

  updateJob(
    id: string,
    patch: Partial<Omit<MaintenanceJob, 'id' | 'jobId'>>,
  ): boolean {
    if (!environment.useMockData) {
      return false;
    }

    const previous = this.jobs().find((job) => job.id === id);
    this.jobs.update((list) =>
      list.map((job) => (job.id === id ? { ...job, ...patch } : job)),
    );

    if (patch.status === 'completed' && previous?.status !== 'completed') {
      this.addRepairHistoryFromJob(this.jobs().find((job) => job.id === id)!);
    }
    return true;
  }

  updateJobStatus(id: string, status: MaintenanceJobStatus): boolean {
    return this.updateJob(id, { status });
  }

  /** POST /Maintenance/jobs/complete */
  completeJobViaApi(
    jobNumber: string,
    completionNotes?: string,
  ): Observable<MaintenanceApiResult> {
    const jobId = jobNumber.trim();
    if (!jobId) {
      return of({ success: false, message: 'Job number is required.' });
    }

    if (environment.useMockData) {
      const updated = this.updateJobStatus(
        this.jobs().find((job) => job.jobId === jobId || job.id === jobId)
          ?.id ?? jobId,
        'completed',
      );
      return of(
        updated
          ? { success: true, message: 'Job completed successfully.' }
          : { success: false, message: 'Job not found.' },
      );
    }

    return this.api
      .post<{ success?: boolean; message?: string }>(
        '/Maintenance/jobs/complete',
        {
          jobNumber: jobId,
          completedByUserID: this.auth.currentUserId(),
          completionNotes: completionNotes?.trim() || null,
        },
      )
      .pipe(
        tap(() => this.refreshFromApi()),
        map((response) => ({
          success: response?.success !== false,
          message:
            response?.message ?? 'Maintenance job completed successfully.',
        })),
        catchError((error) =>
          of(this.toApiError(error, 'Failed to complete maintenance job.')),
        ),
      );
  }

  deleteJob(id: string): boolean {
    if (!environment.useMockData) {
      return false;
    }
    this.jobs.update((list) => list.filter((job) => job.id !== id));
    return true;
  }

  deleteRepairHistoryEntry(id: string): boolean {
    if (!environment.useMockData) {
      return false;
    }
    this.repairHistory.update((list) => list.filter((row) => row.id !== id));
    return true;
  }

  private applyJobs(payload: MaintenanceJobsResponseDto): void {
    const rows = payload.jobs ?? [];
    this.jobs.set(
      rows.map((row, index) => ({
        id: pickString(row, 'jobID', 'id') || String(index + 1),
        jobId: pickString(row, 'jobID', 'JobID') || `MJ-${index + 1}`,
        assetName: pickString(row, 'asset', 'equipment', 'Asset', 'ItemName'),
        assetId: pickString(row, 'assetNumber', 'AssetNumber'),
        type: mapJobType(pickString(row, 'type', 'Type', 'MaintenanceType')),
        description: pickString(
          row,
          'description',
          'Description',
          'ProblemDescription',
        ),
        priority: mapPriority(pickString(row, 'priority', 'Priority')),
        assignedTo: String(
          pickString(row, 'assignedTo', 'AssignedTo', 'AssignedToName') ||
            'Unassigned',
        ),
        sentBy:
          pickString(
            row,
            'reportedBy',
            'ReportedBy',
            'ReportedByName',
            'sentBy',
          ) || undefined,
        startDate: pickDate(
          row,
          'startDate',
          'StartDate',
          'ReportedDate',
          'openedDate',
        ),
        status: mapJobStatus(
          pickString(row, 'status', 'Status', 'JobStatus', 'jobStatus'),
        ),
      })),
    );
  }

  private applyRepairs(payload: RepairHistoryResponseDto): void {
    const rawPayload = payload as Record<string, unknown>;
    const rows = (payload.repairs ??
      (rawPayload['Repairs'] as RepairHistoryResponseDto['repairs']) ??
      []) as unknown[];

    this.repairHistory.set(
      rows
        .map((row, index) => {
          const statusRaw = pickString(
            row,
            'status',
            'Status',
            'JobStatus',
          ).toUpperCase();
          const jobId =
            pickString(row, 'jobID', 'JobID', 'jobId', 'JobNumber') ||
            `MJ-${index + 1}`;
          const assetName = pickString(
            row,
            'asset',
            'Asset',
            'equipment',
            'ItemName',
            'itemName',
          );
          const assetId = pickString(
            row,
            'assetNumber',
            'AssetNumber',
            'assetNumber',
          );
          const description = pickString(
            row,
            'description',
            'Description',
            'ProblemDescription',
            'problemDescription',
          );
          const completedDate = pickDate(
            row,
            'completed',
            'Completed',
            'CompletedDate',
            'completedDate',
          );
          const costRaw = pick(row, 'cost', 'Cost', 'ActualCost', 'actualCost');
          const cost =
            typeof costRaw === 'number'
              ? pickNumber(row, 'cost', 'Cost', 'ActualCost')
              : parseFormattedCurrency(costRaw);

          return {
            id: jobId || String(index + 1),
            jobId,
            assetName,
            assetId,
            repairType: mapJobType(
              pickString(
                row,
                'type',
                'Type',
                'MaintenanceType',
                'maintenanceType',
              ),
            ),
            description,
            technician: pickString(
              row,
              'assignedTo',
              'AssignedTo',
              'AssignedTechnician',
              'Technician',
              'technician',
            ),
            completedDate,
            cost,
            statusRaw,
            status: statusRaw.includes('WARRANTY')
              ? ('warranty' as const)
              : ('completed' as const),
          };
        })
        .filter((row) => {
          if (row.statusRaw !== 'COMPLETED' && row.statusRaw !== 'WARRANTY') {
            return false;
          }
          return !!(row.jobId || row.assetName || row.description);
        })
        .map(({ statusRaw: _statusRaw, ...row }) => row),
    );
  }

  private addRepairHistoryFromJob(job: MaintenanceJob): void {
    this.repairHistory.update((list) => [
      {
        id: newId(),
        jobId: job.jobId,
        assetName: job.assetName,
        assetId: job.assetId,
        repairType: job.type,
        description: job.description,
        technician: job.assignedTo,
        completedDate: new Date().toISOString().slice(0, 10),
        cost: 0,
        status: 'completed',
      },
      ...list,
    ]);
  }

  private filterJobs(
    jobs: MaintenanceJob[],
    query: ListQuery,
  ): MaintenanceJob[] {
    const search = query.search?.trim().toLowerCase();
    if (!search) {
      return jobs;
    }

    return jobs.filter(
      (job) =>
        job.jobId.toLowerCase().includes(search) ||
        job.assetName.toLowerCase().includes(search) ||
        job.description.toLowerCase().includes(search),
    );
  }

  private paginate<T>(items: T[], query: ListQuery): PaginatedResult<T> {
    const page = query.page ?? 1;
    const pageSize = query.pageSize ?? (items.length || 1);
    const start = (page - 1) * pageSize;

    return {
      items: items.slice(start, start + pageSize),
      total: items.length,
      page,
      pageSize,
    };
  }

  private toApiError(error: unknown, fallback: string): MaintenanceApiResult {
    if (error instanceof HttpErrorResponse) {
      const body = error.error;
      if (typeof body === 'object' && body !== null) {
        const message = body['message'] ?? body['error'] ?? body['title'];
        if (message != null && String(message).trim()) {
          return { success: false, message: String(message) };
        }
      }
      if (typeof body === 'string' && body.trim()) {
        return { success: false, message: body };
      }
      return { success: false, message: error.message || fallback };
    }

    return { success: false, message: fallback };
  }
}

function mapJobStatus(value: string): MaintenanceJobStatus {
  const v = value.toLowerCase().replace(/_/g, '-');
  if (v.includes('progress')) return 'in-progress';
  if (v.includes('hold')) return 'on-hold';
  if (v.includes('complete')) return 'completed';
  if (v.includes('cancel')) return 'cancelled';
  if (v.includes('open') || v.includes('pending')) return 'pending';
  return 'pending';
}

function mapJobType(value: string): MaintenanceJobType {
  const v = value.toLowerCase();
  if (v.includes('inspect')) return 'inspection';
  if (v.includes('service')) return 'service';
  if (v.includes('maint')) return 'maintenance';
  return 'repair';
}

function mapPriority(value: string): MaintenancePriority {
  const v = value.toLowerCase();
  if (v.includes('crit')) return 'critical';
  if (v.includes('high')) return 'high';
  if (v.includes('low')) return 'low';
  return 'medium';
}
